import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useState} from 'react';

// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ClusterArtifactNode} from './ClusterArtifactNode';
import {ClusterRunNode} from './ClusterRunNode';
import {artifactMembershipNodeInputType} from './InfoDrawerArtifacts';
import {runNodeInputType} from './InfoDrawerRuns';
import {nodeType} from './LayoutDagUtils';
import {Artifact, Cluster, Dag, Run} from './utils';

type ClusterNodeProps = {
  cluster: Cluster;
  updateDag: (dag: Dag, expandedArtifact: string, expandedRun: string) => void;
  setIsDrawerOpen: (val: boolean) => void;
  setDrawerType: (val: nodeType) => void;
  setArtifactMembershipNodeInput: (
    val: artifactMembershipNodeInputType
  ) => void;
  setRunNodeInput: (val: runNodeInputType) => void;
  filterGeneratedArtifacts: boolean;
  clusterSize?: number;
  directLineage: boolean;
};

export const ClusterNode = ({
  cluster,
  updateDag,
  setIsDrawerOpen,
  setDrawerType,
  setArtifactMembershipNodeInput,
  setRunNodeInput,
  filterGeneratedArtifacts,
  clusterSize,
  directLineage,
}: ClusterNodeProps) => {
  const [filteredRunNodes, setFilteredRunNodes] = useState(cluster.runNodes);
  const [filteredArtifactNodes, setFilteredArtifactNodes] = useState(
    cluster.artifactNodes
  );
  const [searchFilter, setSearchFilter] = useState('');
  useEffect(() => {
    const tempFilteredArtifactNodes: Artifact[] = [];
    const tempFilteredRunNodes: Run[] = [];
    const searchTarget = searchFilter?.toLowerCase().trim();
    cluster.artifactNodes.forEach(node => {
      if (
        searchTarget === '' ||
        searchTarget == null ||
        node.artifactSequenceName.toLowerCase().includes(searchTarget) ||
        node.versionIndex.toString().includes(searchTarget) ||
        node.artifactTypeName.toString().includes(searchTarget)
      ) {
        tempFilteredArtifactNodes.push(node);
      }
    });
    cluster.runNodes.forEach(node => {
      if (
        searchTarget === '' ||
        searchTarget == null ||
        (node.displayName != null &&
          node.displayName.toLowerCase().includes(searchTarget)) ||
        (node.jobType != null &&
          node.jobType.toLowerCase().includes(searchTarget))
      ) {
        tempFilteredRunNodes.push(node);
      }
    });
    setFilteredArtifactNodes(tempFilteredArtifactNodes);
    setFilteredRunNodes(tempFilteredRunNodes);
  }, [searchFilter, cluster.artifactNodes, cluster.runNodes]);
  const isArtifact = cluster.nodeType === nodeType.ARTIFACT;
  return (
    <Tailwind style={{width: '100%', height: '100%'}}>
      <div
        className={`flex h-full w-full flex-col rounded-lg border border-solid border-moon-300 hover:border-teal-400`}>
        <div
          className={`flex h-[42px] w-full flex-shrink-0 items-center rounded-t-lg px-[10px] py-[8px] ${
            isArtifact ? 'bg-[#D0E4FE]' : 'bg-[#C9F5E0]'
          }`}>
          <div className="flex h-[22px] w-[22px] items-center justify-center">
            <Icon
              className={`h-[18px] w-[18px] flex-none text-${
                isArtifact ? 'blue-550' : 'green-600'
              }`}
              name={isArtifact ? 'versions-layers' : 'running-repeat'}
            />
          </div>
          <div className="ml-[4px] flex">
            <div
              className={`font-sans text-[14px] font-normal ${
                isArtifact ? 'text-blue-700' : 'text-green-700'
              }`}>
              {cluster.nodeType + 's'}
            </div>
          </div>
          <div
            className={`moon-500 ml-auto flex	h-[20px] w-[27px] items-center justify-center rounded text-sm font-semibold
          ${
            isArtifact
              ? 'bg-blue-350 text-blue-650'
              : 'bg-green-350 text-green-650'
          }
          `}>
            {isArtifact
              ? filteredArtifactNodes.length
              : filteredRunNodes.length}
          </div>
        </div>

        <div className="flex h-full flex-col gap-[12px] overflow-hidden px-[8px] py-[12px]">
          <TextField
            icon="search"
            placeholder={`Search ${cluster.nodeType}s`}
            value={searchFilter}
            onChange={(value: string) => setSearchFilter(value)}
          />

          <div className="nowheel flex flex-col overflow-y-auto rounded border border-solid border-moon-250">
            {filteredArtifactNodes.map((artNode: Artifact) => (
              <div
                className="flex cursor-pointer text-[14px] hover:bg-moon-100"
                key={`${artNode?.artifactNodeID}_${cluster.id}`}>
                <ClusterArtifactNode
                  artifactNode={artNode}
                  updateDag={updateDag}
                  setIsDrawerOpen={setIsDrawerOpen}
                  setDrawerType={setDrawerType}
                  setArtifactMembershipNodeInput={
                    setArtifactMembershipNodeInput
                  }
                  filterGeneratedArtifacts={filterGeneratedArtifacts}
                  cluster={cluster}
                  clusterSize={clusterSize}
                  directLineage={directLineage}
                />
              </div>
            ))}
            {filteredRunNodes.map((runNode: Run) => (
              <div
                className="flex cursor-pointer text-[14px] hover:bg-moon-100"
                key={`${runNode?.runNodeID}_${cluster.id}`}>
                <ClusterRunNode
                  runNode={runNode}
                  updateDag={updateDag}
                  setIsDrawerOpen={setIsDrawerOpen}
                  setDrawerType={setDrawerType}
                  setRunNodeInput={setRunNodeInput}
                  filterGeneratedArtifacts={filterGeneratedArtifacts}
                  cluster={cluster}
                  clusterSize={clusterSize}
                  directLineage={directLineage}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Tailwind>
  );
};
